import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/context/I18nContext";

const round2 = (n) => Math.round(n * 100) / 100;

export default function VolumeCalculatorDialog({ open, onOpenChange, depth, onApply }) {
  const { t } = useI18n();
  const [shape, setShape] = useState("rectangle");
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [diameter, setDiameter] = useState("");

  const area = useMemo(() => {
    const L = parseFloat(length);
    const W = parseFloat(width);
    const D = parseFloat(diameter);
    if (shape === "rectangle") {
      if (!isFinite(L) || !isFinite(W) || L <= 0 || W <= 0) return 0;
      return round2(L * W);
    }
    if (shape === "round") {
      if (!isFinite(D) || D <= 0) return 0;
      const r = D / 2;
      return round2(Math.PI * r * r);
    }
    if (shape === "oval") {
      if (!isFinite(L) || !isFinite(W) || L <= 0 || W <= 0) return 0;
      return round2(Math.PI * (L / 2) * (W / 2));
    }
    return 0;
  }, [shape, length, width, diameter]);

  const depthNum = parseFloat(depth);
  const volumePreview = isFinite(depthNum) && depthNum > 0 && area > 0 ? round2(area * depthNum) : 0;

  const apply = () => {
    if (area > 0) {
      onApply(area);
      onOpenChange(false);
    }
  };

  const formulaText =
    shape === "rectangle"
      ? t("calc_formula_rect")
      : shape === "round"
        ? t("calc_formula_round")
        : t("calc_formula_oval");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="volume-calc-dialog">
        <DialogHeader>
          <DialogTitle>{t("calc_title")}</DialogTitle>
          <DialogDescription>{t("calc_subtitle")}</DialogDescription>
        </DialogHeader>

        <Tabs value={shape} onValueChange={setShape} className="mt-2">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="rectangle" data-testid="calc-tab-rectangle">
              {t("shape_rectangle")}
            </TabsTrigger>
            <TabsTrigger value="round" data-testid="calc-tab-round">
              {t("shape_round")}
            </TabsTrigger>
            <TabsTrigger value="oval" data-testid="calc-tab-oval">
              {t("shape_oval")}
            </TabsTrigger>
          </TabsList>

          <TabsContent value="rectangle" className="mt-4 grid gap-3 sm:grid-cols-2">
            <div>
              <Label>{t("length_m")}</Label>
              <Input
                type="number"
                step="0.1"
                min="0"
                value={length}
                onChange={(e) => setLength(e.target.value)}
                data-testid="calc-rect-length"
              />
            </div>
            <div>
              <Label>{t("width_m")}</Label>
              <Input
                type="number"
                step="0.1"
                min="0"
                value={width}
                onChange={(e) => setWidth(e.target.value)}
                data-testid="calc-rect-width"
              />
            </div>
          </TabsContent>

          <TabsContent value="round" className="mt-4">
            <div>
              <Label>{t("diameter_m")}</Label>
              <Input
                type="number"
                step="0.1"
                min="0"
                value={diameter}
                onChange={(e) => setDiameter(e.target.value)}
                data-testid="calc-round-diameter"
              />
            </div>
          </TabsContent>

          <TabsContent value="oval" className="mt-4 grid gap-3 sm:grid-cols-2">
            <div>
              <Label>{t("length_m")}</Label>
              <Input
                type="number"
                step="0.1"
                min="0"
                value={length}
                onChange={(e) => setLength(e.target.value)}
                data-testid="calc-oval-length"
              />
            </div>
            <div>
              <Label>{t("width_m")}</Label>
              <Input
                type="number"
                step="0.1"
                min="0"
                value={width}
                onChange={(e) => setWidth(e.target.value)}
                data-testid="calc-oval-width"
              />
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-2 text-xs text-muted-foreground" data-testid="calc-formula">
          {formulaText}
        </div>

        <div className="mt-4 grid gap-2 rounded-md border border-border bg-muted/40 p-3 font-mono-jb text-sm">
          <div className="flex items-baseline justify-between">
            <span className="text-muted-foreground">{t("calc_area_result")}</span>
            <span className="text-base font-semibold" data-testid="calc-area-result">
              {area} m²
            </span>
          </div>
          <div className="flex items-baseline justify-between">
            <span className="text-muted-foreground">{t("calc_volume_preview")}</span>
            <span className="text-base font-semibold text-primary" data-testid="calc-volume-preview">
              {volumePreview} m³
            </span>
          </div>
        </div>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="calc-cancel-btn">
            {t("cancel")}
          </Button>
          <Button onClick={apply} disabled={area <= 0} data-testid="calc-apply-btn">
            {t("apply")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
